import { z } from 'zod';

export const SapAccountAddressSchema = z.object(
  {
    line1: z.string(),
    line2: z.string().optional(),
    city: z.string(),
    country: z.string(),
    postalCode: z.string(),
    email: z.string(),
    phone: z.string(),
  },
  {
    error: (issue) => (issue.input === null ? 'Address is required' : 'Invalid address'),
  }
);
export type SapAccountAddress = z.infer<typeof SapAccountAddressSchema>;

export const SapAccountSchema = z.object({
  accountId: z.string(),
  name: z.string().optional(),
  alternatePaymentName: z.string().optional(),
  objectId: z.string(),
  customerGroupCode: z.string(),
  distributionChannelCode: z.string(),
  currencyCode: z.string(),
  billingAddresses: z.array(SapAccountAddressSchema),
  shippingAddresses: z.array(SapAccountAddressSchema),
});
export type SapAccount = z.infer<typeof SapAccountSchema>;

export const SapPurchasableItemSchema = z.object({
  productId: z.string().nullable(),
  price: z.number(),
  baseQuantity: z.number(),
  description: z.string(),
  currencyCode: z.string(),
  isReleased: z.boolean(),
});
export type SapPurchasableItem = z.infer<typeof SapPurchasableItemSchema>;

const ShippingInstructionsSchema = z.enum(['express', 'economy'], {
  error: (issue) =>
    issue.input === null ? 'Shipping instructions are required' : 'Invalid shipping instructions',
});
export type SapShippingInstructions = z.infer<typeof ShippingInstructionsSchema>;
export const SapNewOrderSchema = z.object({
  accountId: z.string(),
  items: z
    .array(
      z.object({
        productId: z.string(),
        description: z.string(),
        quantity: z.number(),
        price: z.number(),
        currencyCode: z.string(),
      })
    )
    .refine(
      (data) => {
        const hasAtLeastOneItem = data.some((item) => item.quantity > 0);

        return hasAtLeastOneItem;
      },
      {
        message: 'At least one item must be selected',
      }
    ),
  shippingAddress: SapAccountAddressSchema,
  billingAddress: SapAccountAddressSchema,
  shippingInstructions: ShippingInstructionsSchema,
  expectedDeliveryDate: z.string().optional(),
  vatId: z.string().optional(),
  overallDiscount: z.number().optional(),
  notes: z.string().optional(),
  externalReference: z.string().optional(),
});

export type SapNewOrder = z.infer<typeof SapNewOrderSchema>;

export const SapSalesOrderSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  externalReference: z.string().optional(),
  createdAt: z.string(),
  status: z.string(),
  shippingInstructions: ShippingInstructionsSchema.optional(),
  sellThroughTo: z.string().optional(),
  items: z.array(
    z.object({
      productId: z.string(),
      quantity: z.number(),
      description: z.string(),
      price: z.number(),
      currencyCode: z.string(),
    })
  ),
  totalPrice: z.number(),
  currencyCode: z.string(),
  notes: z
    .array(
      z.object({
        type: z.union([z.literal('internal'), z.literal('customer')]),
        content: z.string(),
      })
    )
    .optional(),
});
export type SapSalesOrder = z.infer<typeof SapSalesOrderSchema>;

///Date(1744744979000)/ -> IsoDate(2025-04-15T19:22:59.000Z)
export function fromSapDate(value: string) {
  const timestamp = value.replace(/\/Date\((\d+)\)\//, '$1');
  const date = new Date(Number(timestamp));
  return date.toISOString();
}
